import { useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { useSelfServe } from 'api/useSelfServe';
import { useUserDetails } from 'api/useUserDetails';
import { ETaskId } from 'common/contracts';
import { EChecklistTaskStatus } from 'common/contracts';
import { EEventsType } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { trackMixpanelEvent } from 'utils/mixpanelClient';

import Button from 'components/Button/Button';
import { DialogDescription, DialogHeader, DialogTitle } from 'components/ui';

const Header: React.FC<{ hasSubmitted: boolean }> = ({ hasSubmitted }) => (
  <DialogHeader className="flex flex-col gap-1.5 text-center">
    <DialogTitle className="flex flex-col gap-1.5 text-center my-auto text-2xl">
      {hasSubmitted ? 'Agreement Sent' : 'Sign Agreement'}
    </DialogTitle>
    <DialogDescription className="text-base-mutedForeground font-normal text-sm text-center">
      {hasSubmitted
        ? 'We’ve sent the agreement to your email. You can always come back here if you need us to resend it.'
        : 'Click below and we’ll send the agreement to your email.'}
    </DialogDescription>
  </DialogHeader>
);

export const SignAgreementFormContainer: React.FC = () => {
  const { setTaskStatus, categories } = useChecklistState();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const location = useLocation();
  const publisherId = location.pathname.split('/project/')[1]?.split('/')[0];
  const { sendAgreement } = useSelfServe(publisherId);
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { userDetails } = useUserDetails();

  const taskStatus = categories
    ?.find((c) => c.title === 'Sign agreement')
    ?.tasks.find((t) => t.id === ETaskId.SIGN_AGREEMENT)?.status;

  const hasSubmitted = useMemo(
    () =>
      taskStatus === EChecklistTaskStatus.DONE ||
      taskStatus === EChecklistTaskStatus.PENDING,
    [taskStatus]
  );

  const handleSendAgreement = () => {
    setIsLoadingSubmit(true);
    
    trackMixpanelEvent(EEventsType.DASHBOARD_AGREEMENT_REQUEST_CLICKED, {
      publisher_id: publisherId,
      user_id: userDetails?.userId,
      company_id: userDetails?.company
    });
    trackBigQueryEvent({
      eventName: EEventsType.DASHBOARD_AGREEMENT_REQUEST_CLICKED,
      publisherId: publisherId,
      userId: userDetails?.userId,
      taskName: ETaskId.SIGN_AGREEMENT,
      taskStatus: EChecklistTaskStatus.OPEN
    });

    sendAgreement.mutate(
      {
        userId: userDetails!.userId,
        companyId: userDetails!.company!,
        publisherId
      },
      {
        onSuccess: () => {
          setTaskStatus(ETaskId.SIGN_AGREEMENT, EChecklistTaskStatus.PENDING);
        },
        onError: () => {
          console.error('Error sending agreement');
        },
        onSettled: () => {
          setIsLoadingSubmit(false);
        }
      }
    );
  };

  return (
    <div className="flex flex-col p-6 w-full items-center justify-between text-center">
      <Header hasSubmitted={!!hasSubmitted} />
      <Button
        onClick={handleSendAgreement}
        className="bg-base-primary hover:bg-button-hover-default text-base-primaryForeground font-normal"
        type="submit"
        isLoading={isLoadingSubmit}
      >
        {hasSubmitted ? 'Resend Email' : 'Get Agreement by Email'}
      </Button>
    </div>
  );
};
